import { CartGift, CartItem, CartItemGroupWrapping } from "@tential/ec-gql-schema/models/order"
import { ProductAndSku } from "@tential/ec-gql-schema/types/cart"

/**
 * ギフト、まとめてラッピング、刺繍の設定が競合していないかチェック
 * - ギフト x 刺繍 = OK
 * - まとめてラッピング（まとめてラッピングされるSKU） x ギフト = NG
 * - まとめてラッピング（まとめてラッピングされるSKU） x 刺繍 = NG
 * - まとめてラッピング（まとめてラッピングするSKU） x ギフト = NG
 * - まとめてラッピング（まとめてラッピングするSKU） x 刺繍 = NG
 * @param {ProductAndSku[]} cartList
 * @returns {boolean} falseが発生した場合はシステム不具合の可能性あり、要調査
 */
const checkWrappingAndEmbroiderySetting = (cartList: ProductAndSku[]): boolean => {
  for (const cartItem of cartList) {
    if (cartItem.group_wrappings && cartItem.group_wrappings.length > 0) {
      if (cartItem.gifts && cartItem.gifts.length > 0) {
        console.error("Cannot use group wrapping and gift together", "cartItem: ", cartItem)
        return false
      }
      if (cartItem.embroideries && cartItem.embroideries.length > 0) {
        console.error("Cannot use group wrapping and embroidery together", "cartItem: ", cartItem)
        return false
      }
    }
    if (cartItem.group_wrapped_uniq_id) {
      if (cartItem.gifts && cartItem.gifts.length > 0) {
        console.error("Cannot use group wrapped and gift together", "cartItem: ", cartItem)
        return false
      }
      if (cartItem.embroideries && cartItem.embroideries.length > 0) {
        console.error("Cannot use group wrapped and embroidery together", "cartItem: ", cartItem)
        return false
      }
    }
  }
  return true
}

export const updateCartList = (
  cartList: CartItem[],
  productAndSkus: ProductAndSku[],
  outOfSkuIds: string[],
  outOfSkuWithGifts: CartGift[],
  outOfSkuWithGroupWrappings: CartItemGroupWrapping[],
) => {
  // 在庫切れの商品をカートから削除
  let isGroupWrappedItem = false
  if (outOfSkuIds.length > 0) {
    cartList = cartList.filter((cartItem) => !outOfSkuIds.includes(cartItem.sku_id))
    for (const skuId of outOfSkuIds) {
      // まとめてラッピングの構成商品が在庫切れの場合、ラッピング構成商品とラッピング資材をすべて削除
      const groupWrappingItem = cartList.find(
        (cartItem) =>
          cartItem.group_wrapping?.wrapping_sku_ids.includes(skuId) || cartItem.group_wrapped_uniq_id?.includes(skuId),
      )
      if (groupWrappingItem) {
        cartList = cartList
          .filter((cartItem) => !cartItem.group_wrapping?.wrapping_sku_ids)
          .filter((cartItem) => !cartItem.group_wrapped_uniq_id)
        isGroupWrappedItem = true
      }
    }
  }

  // ギフトの在庫が切れた商品をカートから削除
  if (outOfSkuWithGifts.length > 0) {
    cartList = cartList.filter((cartItem) => {
      if (!cartItem.gift) return true
      const outOfCartItem = outOfSkuWithGifts.find((outOfSkuWithGift) => {
        return !!(
          outOfSkuWithGift.sku_id === cartItem.gift?.sku_id &&
          outOfSkuWithGift.message_sku_id === cartItem.gift?.message_sku_id &&
          outOfSkuWithGift.card_sku_id === cartItem.gift?.card_sku_id
        )
      })
      return !outOfCartItem
    })
  }

  // まとめてラッピング資材が在庫切れの場合、ラッピング構成商品とラッピング資材をすべて削除
  if (outOfSkuWithGroupWrappings.length > 0) {
    cartList = cartList.filter((cartItem) => !outOfSkuIds.includes(cartItem.sku_id))
    for (const giftWrapping of outOfSkuWithGroupWrappings) {
      for (const skuId of giftWrapping.wrapping_sku_ids) {
        const groupWrappingItem = cartList.find(
          (cartItem) =>
            cartItem.group_wrapping?.wrapping_sku_ids.includes(skuId) ||
            cartItem.group_wrapped_uniq_id?.includes(skuId),
        )
        if (groupWrappingItem) {
          cartList = cartList
            .filter((cartItem) => !cartItem.group_wrapping?.wrapping_sku_ids)
            .filter((cartItem) => !cartItem.group_wrapped_uniq_id)
        }
      }
    }
  }

  // filterした結果、まとめてラッピングの整合性が取れなくなった場合、cartListをリセット
  const isExistsGroupWrappingSku = cartList.some((cartItem) => cartItem.group_wrapping?.wrapping_sku_ids)
  const isExistsGroupWrappedSku = cartList.some((cartItem) => cartItem.group_wrapped_uniq_id)
  const isSafeWrappingAndEmbroidery = checkWrappingAndEmbroiderySetting(productAndSkus)

  return {
    isGroupWrappedItem,
    isExistsGroupWrappingSku,
    isExistsGroupWrappedSku,
    isSafeWrappingAndEmbroidery,
    updatedCartList: cartList,
  }
}
